import { ElementType, FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { OnboardingTasksV2 } from '@frontend/api-onboarding-tasks';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Text, Truncated } from '@frontend/design-system';
import { useOnboardingTaskAction } from '../../hooks/use-onboarding-task-action';
import { useOnboardingModuleTasks } from '../../provider/onboarding-module-tasks.provider';
import { getOnboardingTaskTrackingId, TaskCardTrackingIds } from '../../tracking-ids';
import { OnboardingActionSource } from '../../types';
import { getModuleTaskStats, isTaskCompleted } from '../../utils';
import { GlassCard } from '../glass-card';
import { ProgressBar } from '../progress-bar';

interface ModuleTasksCardCompactProps {
  module: OnboardingTasksV2.Types.ModuleRow;
  source?: OnboardingActionSource;
  as?: ElementType;
}
export const ModuleTasksCardCompact: FC<ModuleTasksCardCompactProps> = ({
  module,
  source = 'onboarding-progress-widget',
  as = 'div',
}) => {
  const { t } = useTranslation('onboardingDashboard');
  const { selectedLocationId } = useOnboardingModuleTasks();

  const { completed, hasTaskStarted, total, progress } = useMemo(() => {
    return getModuleTaskStats([module]);
  }, [module]);

  const nextIncompleteTask = module.tasks.find((task) => !isTaskCompleted(task));

  const { renderTaskActionModals, isActionableTask, disableAction, handleTaskClick } = useOnboardingTaskAction({
    task: nextIncompleteTask,
    locationId: selectedLocationId,
  });

  const subTitle = hasTaskStarted ? `${completed} / ${total}` : t('Not Started');

  const showTaskAction = isActionableTask && !disableAction;

  const actionTrackingId =
    nextIncompleteTask && showTaskAction
      ? getOnboardingTaskTrackingId({
          taskName: nextIncompleteTask?.taskName,
          trackingId: TaskCardTrackingIds.click,
          source,
        })
      : undefined;

  return (
    <>
      <GlassCard containerStyles={styles.container} as={as}>
        <GlassCard.Header title={module.moduleName} subTitle={subTitle} headerStyles={styles.header} />
        <div css={styles.body}>
          <div
            css={[styles.content, showTaskAction && styles.clickable]}
            data-trackingid={actionTrackingId}
            onClick={showTaskAction ? handleTaskClick : undefined}
            role={showTaskAction ? 'button' : undefined}
          >
            <Text as='div' size='medium' color='subdued'>
              {completed === 0 ? t('First:') : t('Next:')}
            </Text>
            <Truncated as={Text} size='medium' css={styles.taskTitle}>
              {nextIncompleteTask?.taskTitle}
            </Truncated>
            {showTaskAction && <Icon name='caret-right-small' color='subdued' />}
          </div>
          <ProgressBar
            barColor={theme.colors.neutral50}
            trackColor={theme.colors.neutral10}
            progress={progress}
            showEmptyProgress={false}
          />
        </div>
      </GlassCard>
      {renderTaskActionModals}
    </>
  );
};

const styles = {
  container: css`
    padding: ${theme.spacing(1)};
    gap: ${theme.spacing(0)};
    container-type: inline-size;
  `,
  header: css`
    padding: ${theme.spacing(0, 0.5)};
    justify-content: space-between;
  `,
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  content: css`
    display: flex;
    align-items: center;
    padding: ${theme.spacing(0.5)};
    gap: ${theme.spacing(0.5)};
    margin-bottom: -${theme.spacing(0.5)};
    width: fit-content;
  `,
  taskTitle: css`
    max-width: 240px;
    @container (max-width: 240px) {
      max-width: 150px;
    }
  `,
  clickable: css`
    cursor: pointer;
    border-radius: ${theme.borderRadius.small};
    &:hover {
      background-color: ${theme.colors.white};
    }
  `,
};
